import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
  pickWeightedKey,
  statsFor,
} from "@nav-games/games-core";
import { NAV_LIGHTS, VESSEL_IDS, type Viewpoint } from "@nav-games/nav-lights";

import { DaytimeVesselFigure } from "../../ui/DaytimeVesselFigure";
import { FogPlayButton } from "../../ui/FogPlayButton";
import { HelpButton, HelpPanel } from "../../ui/HelpPanel";
import { NavLightsFigure } from "../../ui/NavLightsFigure";

interface FlashcardsProps {
  onNavigate: (to: string) => void;
}

const MODULE = "nav-lights";
const GAME = "flashcards";
const CARDS_PER_SESSION = 12;
const BEAT_THE_CLOCK_MS = 90_000;
const CHOICES_PER_CARD = 4;
const VIEWPOINTS: readonly Viewpoint[] = [
  "headOn",
  "stern",
  "portBeam",
  "starboardBeam",
];

// Recent answers kept out of the next draw so the spaced-repetition
// picker doesn't resurface the same card within a short window.
// Clamped to pool size - 1 so there is always an eligible card.
const AVOID_WINDOW = Math.min(8, VESSEL_IDS.length - 1);

type SessionMode = "cards" | "timed";

interface Card {
  answer: string;
  viewpoint: Viewpoint;
  choices: readonly string[];
}

function nextCard(recent: readonly string[]): Card {
  const answer = pickWeightedKey(MODULE, VESSEL_IDS, recent);
  const viewpoint = VIEWPOINTS[Math.floor(Math.random() * VIEWPOINTS.length)]!;
  const distractors = new Set<string>();
  while (distractors.size < CHOICES_PER_CARD - 1) {
    const d = VESSEL_IDS[Math.floor(Math.random() * VESSEL_IDS.length)]!;
    if (d !== answer) distractors.add(d);
  }
  const choices = shuffle([answer, ...distractors]);
  return { answer, viewpoint, choices };
}

function viewpointLabel(v: Viewpoint): string {
  switch (v) {
    case "headOn":
      return "Head-on view";
    case "stern":
      return "Stern view";
    case "portBeam":
      return "Port-beam view — looking at her port side";
    case "starboardBeam":
      return "Starboard-beam view — looking at her starboard side";
  }
}

function shuffle<T>(input: readonly T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = arr[i]!;
    const aj = arr[j]!;
    arr[i] = aj;
    arr[j] = ai;
  }
  return arr;
}

export function Flashcards({ onNavigate }: FlashcardsProps) {
  const [sessionMode, setSessionMode] = useState<SessionMode | null>(null);
  if (!sessionMode) {
    return (
      <Picker
        onStart={setSessionMode}
        onBack={() => onNavigate("/play/lights")}
      />
    );
  }
  return (
    <Game
      sessionMode={sessionMode}
      onAgain={() => setSessionMode(sessionMode)}
      onBack={() => onNavigate("/play/lights")}
    />
  );
}

function Picker({
  onStart,
  onBack,
}: {
  onStart: (m: SessionMode) => void;
  onBack: () => void;
}) {
  return (
    <>
      <h2>Identify the vessel</h2>
      <p className="muted">
        Each card shows the lights as seen head-on at night. Pick the matching
        vessel from four choices.
      </p>
      <div className="card">
        <h3>Session length</h3>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <button onClick={() => onStart("cards")}>{CARDS_PER_SESSION} cards</button>
          <button className="secondary" onClick={() => onStart("timed")}>
            Beat the clock — {Math.round(BEAT_THE_CLOCK_MS / 1000)} seconds
          </button>
        </div>
      </div>
      <button className="secondary" onClick={onBack}>
        ← Back to Nav lights
      </button>
    </>
  );
}

interface GameProps {
  sessionMode: SessionMode;
  onAgain: () => void;
  onBack: () => void;
}

function Game({ sessionMode, onAgain, onBack }: GameProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: sessionMode === "cards" ? CARDS_PER_SESSION : 0,
      }),
    [sessionMode],
  );

  const recentRef = useRef<string[]>([]);
  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => {
    const c = nextCard([]);
    recentRef.current = [c.answer];
    return c;
  });
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [feedback, setFeedback] = useState<"right" | "wrong" | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [sessionMode, result]);
  const timeLeftMs = Math.max(
    0,
    BEAT_THE_CLOCK_MS - (now - sessionStartedAt.current),
  );
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    if (timeLeftMs === 0) setResult(recorder.summarise());
  }, [timeLeftMs, sessionMode, result, recorder]);

  const advance = useCallback(() => {
    if (sessionMode === "cards" && index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    const next = nextCard(recentRef.current);
    recentRef.current = [...recentRef.current, next.answer].slice(-AVOID_WINDOW);
    setCard(next);
    setShownAt(Date.now());
    setFeedback(null);
  }, [index, recorder, sessionMode]);

  const onChoose = useCallback(
    (choice: string) => {
      if (feedback || result) return;
      const ms = Date.now() - shownAt;
      const ok = choice === card.answer;
      const attempt: Omit<Attempt, "module"> = {
        char: card.answer,
        ok,
        ms,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setFeedback(ok ? "right" : "wrong");
      // Long dwell so the player can read the reveal — vessel
      // name, fog signal, and rule citation all need to land.
      // Wrong answers get extra time because there's more to learn.
      setTimeout(advance, ok ? 1500 : 3500);
    },
    [advance, card, feedback, recorder, result, shownAt],
  );

  if (result) {
    return <EndCard result={result} onAgain={onAgain} onBack={onBack} />;
  }

  const liveCorrect = recorder.summarise().correct;
  const header =
    sessionMode === "cards"
      ? `Card ${index + 1} of ${CARDS_PER_SESSION}`
      : `${Math.ceil(timeLeftMs / 1000)} s left · ${liveCorrect} right`;

  const vessel = NAV_LIGHTS[card.answer]!;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "nav-lights" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">{header}</p>
      <div className="card" style={{ textAlign: "center" }}>
        <NavLightsFigure
          vessel={vessel}
          viewpoint={card.viewpoint}
          size={220}
          silhouetteHidden
        />
        <p className="small muted" style={{ marginTop: 4 }}>
          {viewpointLabel(card.viewpoint)}
        </p>
        {feedback && (
          <div
            style={{
              marginTop: 8,
              padding: 8,
              borderRadius: 6,
              background:
                feedback === "right"
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
              textAlign: "left",
            }}
          >
            <p style={{ margin: "0 0 4px", fontWeight: 600 }}>
              {feedback === "right" ? "✓ " : "✗ "}
              {vessel.name}
            </p>
            <div style={{ textAlign: "center", margin: "6px 0" }}>
              <DaytimeVesselFigure
                vessel={vessel}
                viewpoint={card.viewpoint}
                size={160}
              />
              <p className="small muted" style={{ margin: "2px 0 0" }}>
                Daytime view — same equipment, lights unlit
              </p>
            </div>
            <p className="small muted" style={{ margin: "2px 0" }}>
              <strong>Rule:</strong> {vessel.rule}
            </p>
            <p className="small muted" style={{ margin: "2px 0" }}>
              <strong>Fog signal:</strong> {vessel.fogSignal}
            </p>
            <FogPlayButton pattern={vessel.fogPattern} />
            {feedback === "wrong" && (
              <p className="small muted" style={{ margin: "6px 0 0" }}>
                {vessel.description}
              </p>
            )}
          </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginTop: 16,
          }}
        >
          {card.choices.map((id) => (
            <button
              key={id}
              className="secondary"
              disabled={!!feedback}
              onClick={() => onChoose(id)}
              style={{
                fontSize: 14,
                padding: "12px 8px",
                textAlign: "left",
                background:
                  feedback && id === card.answer
                    ? "rgba(44, 122, 59, 0.18)"
                    : undefined,
              }}
            >
              {NAV_LIGHTS[id]?.name ?? id}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function EndCard({
  result,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  onAgain: () => void;
  onBack: () => void;
}) {
  const rolling = VESSEL_IDS
    .map((id) => ({ id, ...statsFor(MODULE, id) }))
    .filter((s) => s.attempts >= 3 && s.hitRate < 1)
    .sort((a, b) => a.hitRate - b.hitRate);
  const weakest = rolling.slice(0, 5);

  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}.
        </p>
      </div>
      {weakest.length > 0 && (
        <div className="card">
          <h3>Weakest vessels (rolling)</h3>
          <ul className="small muted">
            {weakest.map((w) => (
              <li key={w.id}>
                {NAV_LIGHTS[w.id]?.name ?? w.id} —{" "}
                {Math.round(w.hitRate * 100)}% over {w.attempts} attempts
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Nav lights
        </button>
      </div>
    </>
  );
}
